/**
 * AdminBadge — "관리자 전용" 영역 표시 공통 심볼
 *
 * 사용처:
 *  - 관리자 전용 페이지 (/ws-admin/* 등) 의 헤더/사이드바
 *  - 일반 화면 안의 관리자 전용 액션 영역 (게시판 카테고리 관리 / 보드 설정 등)
 *
 * 시각: Shield 아이콘 + '관리자' 라벨 chip — indigo (admin 토큰, 관리자 전용 색 통일).
 * variant:
 *  - 'sm' : 12~14px 아이콘, 라벨 text-meta-sm — 인라인 액션 영역용
 *  - 'md' : 16px 아이콘, 라벨 text-label — 페이지/사이드바 헤더용
 *  - 'icon-only' : 라벨 숨김 (tooltip 으로 보완) — 공간 부족할 때
 */
import React from 'react';
import { Shield } from 'lucide-react';
import { Trans, t } from '@lingui/macro';
import { cn } from '../../lib/utils';

interface Props {
  variant?: 'sm' | 'md' | 'icon-only';
  className?: string;
  /** 라벨 텍스트 override. 기본 i18n 'Admin' */
  label?: React.ReactNode;
  title?: string;
}

export const AdminBadge: React.FC<Props> = ({ variant = 'sm', className, label, title }) => {
  const isIconOnly = variant === 'icon-only';
  const isMd = variant === 'md';
  const resolvedTitle = title ?? t`Admin only`;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium border whitespace-nowrap',
        'text-admin bg-admin-bg border-admin/25',
        isMd ? 'h-6 px-2 text-label' : 'h-5 px-1.5 text-meta-sm',
        isIconOnly && 'px-1',
        className,
      )}
      title={resolvedTitle}
      aria-label={resolvedTitle}
    >
      <Shield className={cn(isMd ? 'size-3.5' : 'size-3')} />
      {!isIconOnly && <span>{label ?? <Trans>Admin</Trans>}</span>}
    </span>
  );
};

export default AdminBadge;
